import CurrencyFormatter from "currency-formatter";
import GeneralHelper from "../../utils/GeneralHelper";

export class AssetSaleDealSplitReportItem {
  constructor({
    name,
    value,
    amount,
    assetSaleDealOwner
  }) {
    this.name = name
    this.value = value
    this.amount = amount
    this.assetSaleDealOwner = assetSaleDealOwner
  }

  get amountFormatted() {
    return CurrencyFormatter.format(this.amount, { code: "USD", symbol: "$" })
  }

  static getAllowedAssetDealOwnerValues() {
    return {
      SELLER_ASSET_SALE_DEAL_OWNER: 0,
      BUYER_ASSET_SALE_DEAL_OWNER: 1
    }
  }
}

export default class AssetSaleDealSplitReport {
  constructor({
    currentAssetsSplit = [],
    longTermAssetsSplit = [],
    currentLiabilitiesSplit = [],
    longTermLiabilitiesSplit = []
  }) {
    this.currentAssetsSplit = currentAssetsSplit
    this.longTermAssetsSplit = longTermAssetsSplit
    this.currentLiabilitiesSplit = currentLiabilitiesSplit
    this.longTermLiabilitiesSplit = longTermLiabilitiesSplit
  }

  /*** FLOAT GETTERS ***/

  get totalCurrentAssetsSellerKeeps() {
    const { SELLER_ASSET_SALE_DEAL_OWNER } = AssetSaleDealSplitReportItem.getAllowedAssetDealOwnerValues()
    const currentSplitsSellerKeeps = this.currentAssetsSplit?.filter((reportItem) => {
      return reportItem.assetSaleDealOwner === SELLER_ASSET_SALE_DEAL_OWNER
    })

    return GeneralHelper.sumObjectAttributeFromArray(currentSplitsSellerKeeps, "amount")
  }

  get totalCurrentAssetsBuyerBuys() {
    const { BUYER_ASSET_SALE_DEAL_OWNER } = AssetSaleDealSplitReportItem.getAllowedAssetDealOwnerValues()
    const currentSplitsBuyerBuys = this.currentAssetsSplit?.filter((reportItem) => {
      return reportItem.assetSaleDealOwner === BUYER_ASSET_SALE_DEAL_OWNER
    })

    return GeneralHelper.sumObjectAttributeFromArray(currentSplitsBuyerBuys, "amount")
  }

  get totalLongTermAssetsSellerKeeps() {
    const { SELLER_ASSET_SALE_DEAL_OWNER } = AssetSaleDealSplitReportItem.getAllowedAssetDealOwnerValues()
    const longTermAssetsSellerKeeps = this.longTermAssetsSplit?.filter((reportItem) => {
      return reportItem.assetSaleDealOwner === SELLER_ASSET_SALE_DEAL_OWNER
    })

    return GeneralHelper.sumObjectAttributeFromArray(longTermAssetsSellerKeeps, "amount")
  }

  get totalLongTermAssetsBuyerBuys() {
    const { BUYER_ASSET_SALE_DEAL_OWNER } = AssetSaleDealSplitReportItem.getAllowedAssetDealOwnerValues()
    const longTermAssetsBuyerBuys = this.longTermAssetsSplit?.filter((reportItem) => {
      return reportItem.assetSaleDealOwner === BUYER_ASSET_SALE_DEAL_OWNER
    })

    return GeneralHelper.sumObjectAttributeFromArray(longTermAssetsBuyerBuys, "amount")
  }

  get totalCurrentLiabilitiesSellerKeeps() {
    const { SELLER_ASSET_SALE_DEAL_OWNER } = AssetSaleDealSplitReportItem.getAllowedAssetDealOwnerValues()
    const currentLiabilitiesSellerKeeps = this.currentLiabilitiesSplit?.filter((reportItem) => {
      return reportItem.assetSaleDealOwner === SELLER_ASSET_SALE_DEAL_OWNER
    })

    return GeneralHelper.sumObjectAttributeFromArray(currentLiabilitiesSellerKeeps, "amount")
  }

  get totalCurrentLiabilitiesBuyerBuys() {
    const { BUYER_ASSET_SALE_DEAL_OWNER } = AssetSaleDealSplitReportItem.getAllowedAssetDealOwnerValues()
    const currentLiabilitiesBuyerBuys = this.currentLiabilitiesSplit?.filter((reportItem) => {
      return reportItem.assetSaleDealOwner === BUYER_ASSET_SALE_DEAL_OWNER
    })

    return GeneralHelper.sumObjectAttributeFromArray(currentLiabilitiesBuyerBuys, "amount")
  }

  get totalLongTermLiabilitiesSellerKeeps() {
    const { SELLER_ASSET_SALE_DEAL_OWNER } = AssetSaleDealSplitReportItem.getAllowedAssetDealOwnerValues()
    const longTermLiabilitiesSellerKeeps = this.longTermLiabilitiesSplit?.filter((reportItem) => {
      return reportItem.assetSaleDealOwner === SELLER_ASSET_SALE_DEAL_OWNER
    })

    return GeneralHelper.sumObjectAttributeFromArray(longTermLiabilitiesSellerKeeps, "amount")
  }

  get totalLongTermLiabilitiesBuyerBuys() {
    const { BUYER_ASSET_SALE_DEAL_OWNER } = AssetSaleDealSplitReportItem.getAllowedAssetDealOwnerValues()
    const longTermLiabilitiesBuyerBuys = this.longTermLiabilitiesSplit?.filter((reportItem) => {
      return reportItem.assetSaleDealOwner === BUYER_ASSET_SALE_DEAL_OWNER
    })

    return GeneralHelper.sumObjectAttributeFromArray(longTermLiabilitiesBuyerBuys, "amount")
  }

  get totalTangibleAssetsBuyerBuys() {
    return this.totalCurrentAssetsBuyerBuys + this.totalLongTermAssetsBuyerBuys
  }

  get totalLiabilitiesAssumedByBuyer() {
    return this.totalCurrentLiabilitiesBuyerBuys + this.totalLongTermLiabilitiesBuyerBuys
  }

  get totalAssetsSellerKeeps() {
    return this.totalCurrentAssetsSellerKeeps + this.totalLongTermAssetsSellerKeeps
  }

  get totalLiabilitiesSellerKeeps() {
    return this.totalCurrentLiabilitiesSellerKeeps + this.totalLongTermLiabilitiesSellerKeeps
  }

  /*** FORMATTED GETTERS ***/

  get totalCurrentAssetsBuyerBuysFormatted() {
    return CurrencyFormatter.format(this.totalCurrentAssetsBuyerBuys, { code: "USD", symbol: "$" })
  }

  get totalCurrentAssetsSellerKeepsFormatted() {
    return CurrencyFormatter.format(this.totalCurrentAssetsSellerKeeps, { code: "USD", symbol: "$" })
  }

  get totalLongTermAssetsSellerKeepsFormatted() {
    return CurrencyFormatter.format(this.totalLongTermAssetsSellerKeeps, { code: "USD", symbol: "$" })
  }

  get totalLongTermAssetsBuyerBuysFormatted() {
    return CurrencyFormatter.format(this.totalLongTermAssetsBuyerBuys, { code: "USD", symbol: "$" })
  }

  get totalCurrentLiabilitiesSellerKeepsFormatted() {
    return CurrencyFormatter.format(this.totalCurrentLiabilitiesSellerKeeps, { code: "USD", symbol: "$" })
  }

  get totalCurrentLiabilitiesBuyerBuysFormatted() {
    return CurrencyFormatter.format(this.totalCurrentLiabilitiesBuyerBuys, { code: "USD", symbol: "$" })
  }

  get totalLongTermLiabilitiesSellerKeepsFormatted() {
    return CurrencyFormatter.format(this.totalLongTermLiabilitiesSellerKeeps, { code: "USD", symbol: "$" })
  }

  get totalLongTermLiabilitiesBuyerBuysFormatted() {
    return CurrencyFormatter.format(this.totalLongTermLiabilitiesBuyerBuys, { code: "USD", symbol: "$" })
  }

  get totalTangibleAssetsBuyerBuysFormatted() {
    return CurrencyFormatter.format(this.totalTangibleAssetsBuyerBuys, { code: "USD", symbol: "$" })
  }

  get totalLiabilitiesAssumedByBuyerFormatted() {
    return CurrencyFormatter.format(this.totalLiabilitiesAssumedByBuyer, { code: "USD", symbol: "$" })
  }

  get totalAssetsSellerKeepsFormatted() {
    return CurrencyFormatter.format(this.totalAssetsSellerKeeps, { code: "USD", symbol: "$" })
  }

  get totalLiabilitiesSellerKeepsFormatted() {
    return CurrencyFormatter.format(this.totalLiabilitiesSellerKeeps, { code: "USD", symbol: "$" })
  }

  /*** INSTANCE METHODS ***/

  getTotalAssetsBuyerBuys({ businessGoodwill }, options = { format: true }) {
    const totalAssetsBuyerBuys = businessGoodwill + this.totalTangibleAssetsBuyerBuys
    if (!options.format) {
      return totalAssetsBuyerBuys
    }

    const result = CurrencyFormatter.format(totalAssetsBuyerBuys, {code: "USD", symbol: "$"});
    if(Array.from(result)[0] === "-") return "(" + result.replace(/-/g,"") + ")";
    else return result;
  }

  getEqualsAmountBuyerPaysSellerInCashAndNotes({ businessGoodwill }, options = { format: true }) {
    const equalsAmountBuyerPaysSeller = businessGoodwill + this.totalTangibleAssetsBuyerBuys - this.totalLiabilitiesAssumedByBuyer
    if (!options.format) {
      return equalsAmountBuyerPaysSeller
    }

    const result = CurrencyFormatter.format(equalsAmountBuyerPaysSeller,{code: "USD", symbol: "$"});
    if(Array.from(result)[0] === "-") return "(" + result.replace(/-/g,"") + ")";
    else return result;
  }

  getSellerNetInCashNotesAndRetainedAssets({ businessGoodwill }, options = { format: true }) {
    const equalsAmountBuyerPaysSeller = this.getEqualsAmountBuyerPaysSellerInCashAndNotes({
      businessGoodwill
    }, { format: false })

    if (!options.format) {
      return (equalsAmountBuyerPaysSeller + this.totalAssetsSellerKeeps) - this.totalLiabilitiesSellerKeeps
    }

    return CurrencyFormatter.format(
      (equalsAmountBuyerPaysSeller + this.totalAssetsSellerKeeps) - this.totalLiabilitiesSellerKeeps,
      {
        code: "USD",
        symbol: "$"
      }
    )
  }

  getGoodwillCheck({ businessGoodwill, equityValueOfBusiness }, options = { format: true }) {
    const sellerNetInCashNotesAndRetainedAssets = this.getSellerNetInCashNotesAndRetainedAssets({
      businessGoodwill
    }, { format: false })

    const goodwillCheck = sellerNetInCashNotesAndRetainedAssets - equityValueOfBusiness

    if (!options.format) {
      return goodwillCheck
    }

    const result = CurrencyFormatter.format(goodwillCheck, { code: "USD", symbol: "$" });
    if(Array.from(result)[0] === "-") return "(" + result.replace(/-/g,"") + ")";
    else return result;
  }
}